import type { GameStateManager } from '../game/GameState';
import type { GameData } from '../game/types';

export class ObjectiveLog {
  private isOpen: boolean = true; // Open by default when game starts
  private lastRenderedState: boolean | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData
  ) {}

  update(): void {
    const state = this.gameState.getState();
    const objectives = this.gameData.objectives;
    const activeObjectives: string[] = [];

    // Always show win and lose conditions
    activeObjectives.push(objectives.win_condition);
    activeObjectives.push(objectives.lose_condition);

    // Check for anomalies
    const anomalies = this.gameState.getAnomalies();
    const breachedAnomalies = this.gameState.getBreachedAnomalies();
    const researchers = this.gameState.getResearchers();

    // Check if there are any anomalies in cells
    const hasAnomalies = anomalies.length > 0;

    if (!hasAnomalies) {
      // No anomalies - show accept action objective
      activeObjectives.push(objectives.no_anomalies);
    } else {
      // Check if there are anomalies not being researched
      const anomaliesNotResearched = anomalies.some(anomaly => {
        if (!anomaly.cellId) return false;
        // Check if any researcher is researching this anomaly
        const isBeingResearched = researchers.some(researcher =>
          researcher.currentAction === 'Research' &&
          researcher.location === anomaly.cellId
        );
        return !isBeingResearched;
      });

      if (anomaliesNotResearched) {
        activeObjectives.push(objectives.anomalies_not_researched);
      }

      // Check if there's an anomaly with 3 properties found
      const hasFullyUnderstoodAnomaly = anomalies.some(anomaly => {
        if (!anomaly.cellId) return false;
        // Check if it has 3 properties and is not being researched
        const isFullyUnderstood = anomaly.knownProperties.length >= 3;
        const isBeingResearched = researchers.some(researcher =>
          researcher.currentAction === 'Research' &&
          researcher.location === anomaly.cellId
        );
        return isFullyUnderstood && !isBeingResearched;
      });

      if (hasFullyUnderstoodAnomaly) {
        activeObjectives.push(objectives.fully_understood_anomaly);
      }
    }

    // Check for breaches
    if (breachedAnomalies.length > 0) {
      activeObjectives.push(objectives.breach_active);
    }

    // Check if only one researcher
    if (researchers.length === 1) {
      activeObjectives.push(objectives.only_one_researcher);
    }

    // Check if no chemicals and anomalies exist
    if (state.chemicals === 0 && hasAnomalies) {
      activeObjectives.push(objectives.no_chemicals_with_anomalies);
    }

    // Check for visible Lethal property
    const hasVisibleLethal = anomalies.some(anomaly =>
      anomaly.knownProperties.includes('Lethal')
    );
    if (hasVisibleLethal) {
      activeObjectives.push(objectives.lethal_property_visible);
    }

    // Check for insane researchers (sanity < half max)
    const hasInsaneResearchers = researchers.some(researcher =>
      researcher.currentSanity < researcher.data.sanity / 2
    );
    if (hasInsaneResearchers) {
      activeObjectives.push(objectives.insane_researchers);
    }

    this.render(activeObjectives);
  }

  private render(objectives: string[]): void {
    // Only update DOM if state changed or first render
    if (this.lastRenderedState === this.isOpen && this.lastRenderedState !== null) {
      // State hasn't changed, no need to re-render
      return;
    }
    
    this.lastRenderedState = this.isOpen;
    
    const arrow = this.isOpen ? '▼' : '▶';
    const titleText = `Objectives ${arrow}`;
    const titleHtml = `<div class="objective-log-title">${titleText}</div>`;

    if (objectives.length === 0) {
      this.container.innerHTML = titleHtml;
      this.attachTitleClickHandler();
      return;
    }

    const objectivesList = objectives.map(obj => 
      `<div class="objective-item">${obj}</div>`
    ).join('');

    const contentStyle = this.isOpen ? '' : ' style="display: none;"';

    this.container.innerHTML = `
      ${titleHtml}
      <div class="objective-log-content"${contentStyle}>
        ${objectivesList}
      </div>
    `;
    
    this.attachTitleClickHandler();
  }

  private attachTitleClickHandler(): void {
    const titleElement = this.container.querySelector('.objective-log-title') as HTMLElement;
    if (titleElement && !titleElement.onclick) {
      titleElement.onclick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.isOpen = !this.isOpen;
        
        // Update display immediately without re-rendering
        const contentElement = this.container.querySelector('.objective-log-content') as HTMLElement;
        if (contentElement) {
          contentElement.style.display = this.isOpen ? '' : 'none';
        }
        
        // Update arrow
        const arrow = this.isOpen ? '▼' : '▶';
        titleElement.textContent = `Objectives ${arrow}`;
        
        // Mark that we need to re-render on next update to keep everything in sync
        this.lastRenderedState = null;
      };
    }
  }
}

